import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { to: "/", label: "首页" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const solid = !overlay || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setMenuOpen(false);
  };

  const primaryText = solid ? "text-foreground-950" : "text-background-50";
  const subText = solid ? "text-secondary-600" : "text-background-200";

  return (
    <header
      className={`fixed left-0 top-0 z-50 w-full transition-colors duration-300 ${
        solid
          ? "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-foreground-950/55 to-transparent"
      }`}
    >
      <div className="flex h-[72px] w-full items-center gap-3 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 pr-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600">
            <span className="font-label text-lg font-semibold leading-none text-accent-400">51</span>
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-heading text-[16px] font-bold tracking-tight ${primaryText}`}>
              51视频每日大赛
            </span>
            <span className={`mt-1 font-label text-[10px] tracking-[0.3em] ${subText}`}>
              今日黑榜合集
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navItems.slice(0, 1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? solid
                      ? "bg-primary-50 text-primary-700"
                      : "bg-background-50/15 text-accent-300"
                    : solid
                      ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                      : "text-background-100 hover:text-background-50"
                }`
              }
            >
              首页
            </NavLink>
          ))}

          <div className="group relative">
            <button
              type="button"
              className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                solid
                  ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                  : "text-background-100 hover:text-background-50"
              }`}
            >
              分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            <div className="invisible absolute left-0 top-full w-64 translate-y-1 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${category.icon} text-base`} />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="font-medium">{category.name}</span>
                    <span className="text-[11px] text-foreground-400">{category.countLabel}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {navItems.slice(1).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? solid
                      ? "bg-primary-50 text-primary-700"
                      : "bg-background-50/15 text-accent-300"
                    : solid
                      ? "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                      : "text-background-100 hover:text-background-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={handleSubmit}
            className={`hidden h-10 items-center gap-2 rounded-full border px-3 md:flex ${
              solid
                ? "border-background-300 bg-background-100"
                : "border-background-50/25 bg-background-50/10"
            }`}
          >
            <span
              className={`flex h-6 w-6 items-center justify-center ${
                solid ? "text-foreground-400" : "text-background-200"
              }`}
            >
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索视频标题或标签"
              aria-label="搜索视频"
              className={`w-40 bg-transparent text-sm outline-none lg:w-52 ${
                solid
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-200/80"
              }`}
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-[12px] font-semibold text-primary-950 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors md:hidden ${
              solid
                ? "border-background-300 text-foreground-700"
                : "border-background-50/25 text-background-50"
            }`}
          >
            <i className="ri-search-line text-lg" />
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors lg:hidden ${
              solid
                ? "border-background-300 text-foreground-800"
                : "border-background-50/25 text-background-50"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="anim-fade-in border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={handleSubmit} className="mb-4 flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
            <span className="flex h-6 w-6 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索视频标题或标签"
              aria-label="搜索视频"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>

          <div className="grid grid-cols-2 gap-2">
            <Link to="/" className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800">
              首页
            </Link>
            <Link to="/search" className="rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800">
              搜索
            </Link>
          </div>

          <p className="mb-2 mt-4 font-label text-[11px] tracking-[0.24em] text-secondary-600">赛事分类</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-1.5 rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700"
              >
                <i className={`${category.icon} text-sm text-primary-600`} />
                {category.name}
              </Link>
            ))}
          </div>

          <Link to="/about" className="mt-4 block rounded-md bg-background-100 px-3 py-2.5 text-sm font-medium text-foreground-800">
            关于本站
          </Link>
        </div>
      )}
    </header>
  );
}